import { APITypesV1 } from "@cur8/api-client";
import {
  Button,
  Icon16AlertTriangle,
  Icon16Checkmark,
  Toggle,
  Typography,
} from "@cur8/maneki";
import { Measurement } from "@cur8/rich-entity";
import { UserURI } from "@cur8/uri";
import { UseMutationResult } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import {
  SeverityLevel,
  useAppInsights,
} from "render/context/AppInsightsContext";
import { useMSAL } from "render/context/MSALContext";
import {
  CONCIERGE_GENERATOR,
  MetricConfig,
  getImperialConversion,
  getPreferImperial,
} from "./measurementConfig";
import { CONVERSION_DEFS, isOutOfRange } from "./measurementConversion";
import styles from "./styles.module.sass";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type StoreMutation = UseMutationResult<
  APITypesV1.Measurement,
  unknown,
  APITypesV1.CreateMeasurementRequest
>;

interface MeasurementEntryProps {
  config: MetricConfig;
  visitId: string;
  isAutoImported: boolean;
  existing: Measurement | undefined;
  riskRanges: APITypesV1.RiskRange[] | undefined;
  countryCode: string | undefined;
  storeMeasurement: StoreMutation;
  onBack: () => void;
  onDone: () => void;
}

export function MeasurementEntry({
  config,
  visitId,
  isAutoImported,
  existing,
  riskRanges,
  countryCode,
  storeMeasurement,
  onBack,
  onDone,
}: MeasurementEntryProps) {
  const { auth } = useMSAL();
  const appInsights = useAppInsights();

  // Imperial conversion available for this metric + country (null = metric only)
  const imperialConv = getImperialConversion(config, countryCode);

  const preferImperial =
    !!imperialConv && getPreferImperial(config, countryCode);

  const [showImperial, setShowImperial] = useState(preferImperial);

  // The active conversion def (null when in metric mode)
  const convDef =
    showImperial && imperialConv ? CONVERSION_DEFS[imperialConv] : null;

  // Initialize display values from an existing measurement (always SI from API)
  const [values, setValues] = useState<string[]>(() => {
    if (convDef) {
      const siDim = existing?.dimensions.find(
        (d) => d.name === config.dimensions[0].name
      );
      if (!siDim) {
        return convDef.fields.map(() => "");
      }
      return convDef.toDisplay(siDim.value).map(String);
    }
    return config.dimensions.map((dimConfig) => {
      const dim = existing?.dimensions.find((d) => d.name === dimConfig.name);
      return dim != null ? String(dim.value) : "";
    });
  });

  const [verifiedRisk, setVerifiedRisk] = useState(false);

  // Toggle between metric and imperial, converting current input values
  const handleToggleImperial = useCallback(() => {
    if (!imperialConv) {
      return;
    }
    const def = CONVERSION_DEFS[imperialConv];
    const targetImperial = !showImperial;

    if (targetImperial) {
      const si = parseFloat(values[0]);
      setValues(
        !isNaN(si) ? def.toDisplay(si).map(String) : def.fields.map(() => "")
      );
    } else {
      const parsed = values.map(parseFloat);
      setValues(
        parsed.every((v) => !isNaN(v))
          ? [String(def.toSI(parsed))]
          : config.dimensions.map(() => "")
      );
    }

    setShowImperial(targetImperial);
  }, [showImperial, imperialConv, values, config.dimensions]);

  // SI values for risk range checks and the API payload
  const siValues: Array<number | null> = convDef
    ? (() => {
        const parsed = values.map(parseFloat);
        if (parsed.some(isNaN)) {
          return config.dimensions.map(() => null);
        }
        return [convDef.toSI(parsed)];
      })()
    : values.map((v) => (v === "" ? null : parseFloat(v)));

  const allFilled = siValues.every((v) => v !== null && !isNaN(v as number));

  const riskWarnings: string[] = [];
  if (riskRanges && allFilled) {
    config.dimensions.forEach((dimConfig, i) => {
      const val = siValues[i];
      if (val == null) {
        return;
      }
      const range = riskRanges.find((r) => r.dimensionName === dimConfig.name);
      if (!range) {
        return;
      }
      if (isOutOfRange(val, range)) {
        riskWarnings.push(
          `${dimConfig.name}: ${val} ${dimConfig.unit} is outside normal range`
        );
      }
    });
  }

  const hasRiskWarning = riskWarnings.length > 0;
  const canSave = !!auth && allFilled && (!hasRiskWarning || verifiedRisk);

  const handleSave = useCallback(() => {
    if (!canSave || !auth) {
      appInsights.trackTrace({
        message: "Measurement save blocked on press",
        severityLevel: SeverityLevel.Warning,
        properties: {
          metricName: config.metricName,
          visitId,
          canSave,
          hasAuth: !!auth,
          allFilled,
          hasRiskWarning,
          verifiedRisk,
        },
      });
      return;
    }

    appInsights.trackTrace({
      message: "Measurement save attempted",
      severityLevel: SeverityLevel.Information,
      properties: {
        metricName: config.metricName,
        visitId,
        hasRiskWarning,
        verifiedRisk,
      },
    });

    const dimensions: APITypesV1.CreateMeasurementDimension[] =
      config.dimensions.map((dimConfig, i) => ({
        name: dimConfig.name,
        value: siValues[i] as number,
        unit: dimConfig.unit,
      }));

    storeMeasurement.mutate(
      {
        sourceUri: new UserURI(auth.tenantId, auth.uniqueId).toString(),
        generator: CONCIERGE_GENERATOR,
        metricName: config.metricName,
        timestampStart: new Date().toISOString(),
        type: null,
        dimensions,
        visitId,
      },
      { onSuccess: onDone }
    );
  }, [
    canSave,
    auth,
    visitId,
    config,
    siValues,
    storeMeasurement,
    onDone,
    appInsights,
    allFilled,
    hasRiskWarning,
    verifiedRisk,
  ]);

  // Display fields: either the imperial conversion fields or the metric dimensions
  const displayFields: ReadonlyArray<{ name: string; unit: string }> = convDef
    ? convDef.fields
    : config.dimensions;

  return (
    <div className={styles.entry}>
      {imperialConv && (
        <div className={styles.unitSwitcher}>
          <button
            type="button"
            className={styles.unitOption}
            data-active={!showImperial || undefined}
            onClick={showImperial ? handleToggleImperial : undefined}
          >
            {config.dimensions[0].unit}
          </button>
          <button
            type="button"
            className={styles.unitOption}
            data-active={showImperial || undefined}
            onClick={!showImperial ? handleToggleImperial : undefined}
          >
            {CONVERSION_DEFS[imperialConv].label}
          </button>
        </div>
      )}

      {displayFields.map((field, i) => (
        <DimensionInput
          key={field.name}
          name={field.name}
          unit={field.unit}
          value={values[i]}
          onChange={(val) => {
            const next = [...values];
            next[i] = val;
            setValues(next);
          }}
        />
      ))}

      {hasRiskWarning && (
        <div className={styles.riskWarning}>
          <div className={styles.riskWarningHeader}>
            <Icon16AlertTriangle />
            <Typography variant="label-s">
              Value outside normal range
            </Typography>
          </div>
          {riskWarnings.map((w) => (
            <Typography key={w} variant="body-s">
              {w}
            </Typography>
          ))}
          <div className={styles.verifyRow}>
            <Toggle isSelected={verifiedRisk} onChange={setVerifiedRisk} />
            <Typography variant="body-s">I've verified this value</Typography>
          </div>
        </div>
      )}

      {isAutoImported && !existing && (
        <div className={styles.waitingForDevice}>
          <Typography variant="body-s">
            Waiting for device to send measurement
          </Typography>
        </div>
      )}

      <div className={styles.buttonRow}>
        <Button variant="secondary" onPress={onBack} fillContainer>
          Back
        </Button>
        <Button
          variant="primary"
          onPress={handleSave}
          fillContainer
          state={
            !canSave || storeMeasurement.isPending ? "disabled" : undefined
          }
        >
          {storeMeasurement.isPending
            ? "Saving..."
            : isAutoImported
              ? "Manually overwrite"
              : "Save"}
        </Button>
      </div>

      {storeMeasurement.isError && (
        <Typography variant="body-s" color="warning">
          Failed to save. Please try again.
        </Typography>
      )}

      {storeMeasurement.isSuccess && (
        <div className={styles.successRow}>
          <Icon16Checkmark />
          <Typography variant="body-s">Saved</Typography>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface DimensionInputProps {
  name: string;
  unit: string;
  value: string;
  onChange: (val: string) => void;
}

function DimensionInput({ name, unit, value, onChange }: DimensionInputProps) {
  return (
    <div className={styles.dimensionInput}>
      <label htmlFor={name} className={styles.dimensionLabel}>
        <Typography variant="label-s">{name}</Typography>
        <Typography variant="body-s" color="subtle">
          {unit}
        </Typography>
      </label>
      <input
        id={name}
        className={styles.input}
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${unit}`}
      />
    </div>
  );
}
